import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useClientV2} from "../../../../utils/hooks";
import {useCustomerIdentity} from "../../../../redux/auth/login/customer-identity";
import {AxiosError} from "axios";
import {CreateCustomerStoreServiceOrderDisputeApiPayload} from "./types";
import {message} from "antd";
import {
  GET_STORE_SERVICE_CUSTOMER_ORDER_RQ_KEY,
  GET_STORE_SERVICE_CUSTOMER_ORDERS_RQ_KEY,
} from "../constants";

/**
 * v2 `CreateDisputeDto.offeringType` is validated with
 * `@IsEnum(ConsolidatedOfferingTypeEnum)`, so it must be the exact enum member
 * — v1's `offering_type: "product-service"` fails the validation pipe.
 */
const V2_PRODUCT_SERVICE_OFFERING_TYPE = "PRODUCT_SERVICE";

/**
 * Rewrites the drawer's v1 form-data into the v2 body.
 *
 * The drawer builds `offering_type` / `resourceId` / `description` plus repeated
 * `file` parts. v2 keeps `resourceId`, `description` and `file` verbatim (still
 * 1-3 files, still the field name `file`) and only renames `offering_type` ->
 * `offeringType` with the enum value. Rebuilding here rather than in the drawer
 * keeps the consumer untouched.
 */
function toV2DisputeFormData(source: FormData): FormData {
  const next = new FormData();

  Array.from(source.entries()).forEach(([key, value]) => {
    if (key === "offering_type" || key === "offeringType") {
      return;
    }
    next.append(key, value);
  });

  next.append("offeringType", V2_PRODUCT_SERVICE_OFFERING_TYPE);

  return next;
}

/**
 * Open a dispute on a store-service order (customer) — v2
 * `POST dispute/customer?customerEmail=...`.
 *
 * The path is byte-identical to v1, but two things differ:
 *
 * 1. `CustomerDisputeController` is deliberately outside any auth guard and
 *    identifies the customer from a **required** `customerEmail` query param
 *    (the controller 400s without it), so the signed-in customer's email is
 *    read from the auth session the same way `useGetCustomerStoreServicesOrders`
 *    does. The bearer token is still sent by `useClientV2` and is simply
 *    ignored by that route.
 * 2. The body field `offering_type` becomes `offeringType: "PRODUCT_SERVICE"`.
 *
 * v2 answers `{status, message, statusCode, data: DisputeResponseDto}`; the
 * declared `{data: any}` result already matches, and nothing reads it — the
 * drawer only uses `onSuccess`. Signature, query-key invalidations and the
 * error toast are unchanged.
 */
export const useCreateCustomerStoreServiceOrderDisputeApi = () => {
  const queryClient = useQueryClient();
  const {emailAddress: customerEmail} = useCustomerIdentity();

  const client = useClientV2();
  return useMutation<
    {data: any},
    AxiosError<
      {
        message: string;
      },
      any
    >,
    CreateCustomerStoreServiceOrderDisputeApiPayload,
    unknown
  >({
    mutationFn: (payload: CreateCustomerStoreServiceOrderDisputeApiPayload) => {
      if (!customerEmail) {
        return Promise.reject(
          new Error("We couldn't find your email address. Please sign in again.")
        );
      }

      return client(
        `dispute/customer?customerEmail=${encodeURIComponent(customerEmail)}`,
        {
          method: "POST",
          data: toV2DisputeFormData(payload.formData),
        }
      );
    },
    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
    },
    onSettled: (d, e, v) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_STORE_SERVICE_CUSTOMER_ORDERS_RQ_KEY, {statusId: 4, page: 1}],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_STORE_SERVICE_CUSTOMER_ORDER_RQ_KEY, String(v.resourceId)],
        }),
      ]),
  });
};
